/** @jsxImportSource @emotion/react */
import { useEffect, useState } from 'react'
import { Button } from 'react-bootstrap'
import moment from 'moment'
import { Formik } from 'formik'
import * as Yup from 'yup'

import InputComponent from 'components/atoms/Input'
import {
  useBypassStripePaymentMutation,
  useCreateCheckoutMutation,
  useInitiateManualQuestionnaireMutation,
  useResendPaymentLinkMutation,
  useSendPatientPaperworkEmailMutation,
  useSetOrderFollowUpDateMutation,
} from 'store/services/labOrder'
import { useSendLabNoteMutation } from 'store/services/notes'
import { useGetPatientBalancesQuery } from 'store/services/patientTransactions'
import { FollowUpNamespace, Order } from 'store/types'
import { toast } from 'react-toastify'
import Toast from '../../../atoms/Toast'
import { confirm } from 'components/molecules/Confirmation'
import { ReceivePrepaymentModal } from 'components/lab/billingPayment/ReceivePrepaymentModal'
import { Can } from 'components/lab/Can'
import { UserPermission } from 'components/lab/user/permissions'
import { FollowUpForm } from './FollowUpForm'
import { Notes } from './Notes'

interface IProps {
  order: Order
}

const formValidation = Yup.object().shape({
  followUpDate: Yup.date().required('Required'),
  notes: Yup.string(),
  humanFollowUpRequired: Yup.boolean(),
  humanFollowUpReason: Yup.string().when('humanFollowUpRequired', {
    is: true,
    then: Yup.string().trim().required('Required when human follow-up is selected'),
  }),
})

const money = (value: number) => `$${Number(value || 0).toFixed(2)}`

export const completedStepDescription = {
  patient: 'Patient Information',
  insurance: 'Insurance Information',
  sleepinessScale: 'Sleepiness Scale',
  infoSheet: 'Information Sheet',
  signatureRequests: 'Signature Requests',
  phiDisclosure: 'PHI Disclosure',
  textMsgConsent: 'Text Message Consent',
  policiesResponsibilities: 'Policies & Responsibilities',
  patientRightsResponsibilities: 'Patient Rights & Responsibilities',
  creditCardAgreement: 'Credit Card Agreement',
}

export const PatientScheduling = ({ order }: IProps) => {
  const [email, setEmail] = useState('')
  const [receivePrepaymentOpen, setReceivePrepaymentOpen] = useState(false)
  const [sendPatientPaperworkEmail] = useSendPatientPaperworkEmailMutation()
  const [bypassStripePayment] = useBypassStripePaymentMutation()
  const [initiateManualQuestionnaire] = useInitiateManualQuestionnaireMutation()
  const [createCheckout] = useCreateCheckoutMutation()
  const [setOrderFollowUpDate] = useSetOrderFollowUpDateMutation()
  const [sendLabNote] = useSendLabNoteMutation()
  const [resendPaymentLink, { isLoading: isSendingPaymentLinkSms }] = useResendPaymentLinkMutation()

  const isPaymentInvalidated = Boolean(order?.hst?.paymentInvalidated)
  const isBypassPayment = Boolean(order?.hst?.paymentBypassed)
  const mobilePhone = order?.patient?.mobilePhone || ''

  const patientId = Number(order?.patientId)
  const { data: balances } = useGetPatientBalancesQuery({ patientId }, { skip: !patientId })
  const copay = Number(order?.copay || 0)
  const unappliedPayment = Number(balances?.unappliedPayment || 0)
  // A card is retained on Stripe once the patient completes the checkout/setup session
  // (payment mode saves the card via the completed charge, setup mode saves it outright).
  const hasCardOnFile =
    order?.hst?.paymentSession?.status === 'complete' && Boolean(order?.hst?.paymentSession?.customer)
  const schedulingFollowUpNamespaces = [FollowUpNamespace.HST_PAPERWORK, FollowUpNamespace.HST_PAYMENT]

  const followUps = order?.followUpsList
    ?.filter((f) => schedulingFollowUpNamespaces.includes(f.namespace))
    ?.sort((a, b) => moment(b.addedAt).diff(moment(a.addedAt)))

  const followUpDate = followUps?.[0]?.date ? moment(followUps?.[0]?.date).toDate() : ''
  const latestFollowUp = followUps?.[0]

  useEffect(() => {
    const checkoutAmount = Number(order.hst?.paymentSession?.amount_total) / 100
    const sessionMode = order.hst?.paymentSession?.mode
    const checkoutAmountCorrect =
      checkoutAmount === Number(order?.copay) ||
      (Number(order?.copay) === 0 && checkoutAmount === 1)

    const hasCustomer = order.hst?.paymentSession?.customer

    const shouldCheckAmount = sessionMode !== 'setup'

    if (
      order &&
      !hasCustomer &&
      (!order.hst?.paymentSession ||
        moment().isAfter(moment(order.hst?.paymentSession?.expires_at * 1000)) ||
        (shouldCheckAmount && !checkoutAmountCorrect))
    ) {
      createCheckout({
        orderId: Number(order.id),
        amount: Number(order?.copay),
      })
    } else {
    }
  }, [order, createCheckout])

  const onQuestionnaireClick = async () => {
    try {
      await sendPatientPaperworkEmail({
        orderId: Number(order.id),
        email: order.patient.email,
        method: 'email',
      }).unwrap()
      toast.success('Questionnaire sent successfully')
    } catch (e) {
      toast.error('There was a problem sending the questionnaire')
    }
  }

  const onManualQuestionnaireClick = async () => {
    try {
      await confirm('', {
        title: 'Are you sure?',
        description: 'Are you sure you want to proceed with manual questionnaire?',
        yesAction: 'Yes',
        noAction: 'No',
      })
      await initiateManualQuestionnaire({
        orderId: Number(order.id),
      })
    } catch {}
  }

  const onTextPaymentLinkClick = async () => {
    if (!mobilePhone) {
      Toast({ type: 'error', label: 'Patient mobile phone number not found.' })
      return
    }

    try {
      await resendPaymentLink({
        orderId: Number(order.id),
      }).unwrap()

      Toast({ type: 'success', label: 'Payment link sent by text message.' })
    } catch (error: unknown) {
      const paymentLinkError = error as { data?: { message?: string }; message?: string }
      const errorMessage =
        paymentLinkError?.data?.message ||
        paymentLinkError?.message ||
        'There was a problem sending the payment link.'
      Toast({ type: 'error', label: String(errorMessage) })
    }
  }

  const advanceToShipping = async () => {
    try {
      await bypassStripePayment({ orderId: Number(order.id) }).unwrap()
      Toast({
        type: 'success',
        label: isPaymentInvalidated
          ? 'Order moved to Device Shipping bucket.'
          : 'Order moved to shipping.',
      })
    } catch (err) {
      Toast({ type: 'error', label: 'Error proceeding to shipping.' })
    }
  }

  const onProceedClick = () => {
    if (isPaymentInvalidated) {
      confirm('', {
        title: 'Move to Device Shipping',
        description:
          'This will move the order to Device Shipping bucket. Make sure you have re-contacted the patient and added your notes.',
        yesAction: 'Yes, move to Device Shipping',
        noAction: 'Cancel',
      })
        .then(advanceToShipping)
        .catch(() => {})
      return
    }

    const warnings: string[] = []
    if (!hasCardOnFile) {
      warnings.push('There is no card on file for this patient.')
    }
    if (unappliedPayment < copay) {
      warnings.push(
        `The patient's unapplied balance (${money(unappliedPayment)}) is less than the copay (${money(copay)}).`,
      )
    }

    confirm('', {
      title: warnings.length ? 'Warning' : 'Proceed to Shipping',
      description: warnings.length
        ? `${warnings.join(' ')} Are you sure you want to proceed to shipping?`
        : 'This will proceed the order to shipping.',
      yesAction: 'Yes, proceed to shipping',
      noAction: 'Cancel',
    })
      .then(advanceToShipping)
      .catch(() => {})
  }

  return (
    <div className="w-100">
      <div className="row">
        <div className="col-sm-10 p-0 d-flex align-items-center">
          <h4 className="m-0">Patient Scheduling</h4>
          <Button
            variant="primary"
            className="ml-3"
            onClick={onQuestionnaireClick}
            disabled={
              order?.hst?.paperwork?.meta?.finishedAt ||
              !(order?.patient?.email || order?.patient?.mobilePhone)
            }>
            Send Questionnaire
          </Button>
          <Button
            variant="outline-primary"
            className="ml-2"
            disabled={order?.hst?.paperwork?.meta?.finishedAt}
            onClick={onManualQuestionnaireClick}>
            Manual Questionnaire
          </Button>
        </div>
      </div>
      <div className="row">
        <div className="col-sm-6 mt-3 align-items-end">
          <div
            className="p-3 d-flex align-items-end"
            css={{ background: '#F5F5F5', width: '100%', height: '100%' }}>
            <Formik
              enableReinitialize={true}
              validationSchema={formValidation}
              initialValues={{
                followUpDate,
                notes: '',
                humanFollowUpRequired: Boolean(order?.patient?.voiceOptOut) || (latestFollowUp?.humanFollowUpRequired ?? false),
                humanFollowUpReason:
                  latestFollowUp?.humanFollowUpReason ||
                  (order?.patient?.voiceOptOut ? 'Patient opted out of automated voice calls' : ''),
              }}
              onSubmit={async (values, { resetForm }) => {
                await Promise.all([
                  setOrderFollowUpDate({
                    orderId: Number(order.id),
                    namespace: latestFollowUp?.namespace ?? FollowUpNamespace.HST_PAPERWORK,
                    date: moment(values.followUpDate).format('YYYY-MM-DD'),
                    comment: values.notes,
                    humanFollowUpRequired: values.humanFollowUpRequired,
                    humanFollowUpReason: values.humanFollowUpRequired
                      ? values.humanFollowUpReason
                      : null,
                  }).unwrap(),
                  await sendLabNote({
                    orderId: Number(order.id),
                    text: `${values.notes} (follow-up: ${moment(values.followUpDate).format(
                      'MM/DD/YYYY',
                    )})`,
                    type: 'scheduling',
                  }).unwrap(),
                ])
                resetForm()
              }}>
              {({ values, setFieldValue, errors, touched }) => (
                <FollowUpForm
                  {...{ values, setFieldValue, errors, touched }}
                  forceHumanFollowUp={Boolean(order?.patient?.voiceOptOut)}
                />
              )}
            </Formik>
          </div>
        </div>
        <div className="col-sm-6 p-3 mt-3 align-items-end" css={{ background: '#F5F5F5' }}>
          <div className="d-flex justify-content-between">
            <strong>Send payment link</strong>
            <div className="d-flex flex-wrap justify-content-end" css={{ gap: '0.5rem' }}>
              <Button
                variant="primary"
                onClick={onTextPaymentLinkClick}
                disabled={!mobilePhone || isSendingPaymentLinkSms}>
                Resend Payment Link
              </Button>
              <Can permission={UserPermission.POST_PATIENT_PAYMENTS}>
                <Button variant="secondary" onClick={() => setReceivePrepaymentOpen(true)}>
                  Receive Prepayment
                </Button>
              </Can>
              <Button variant="secondary" onClick={onProceedClick}>
                {isPaymentInvalidated ? 'Move to Device Shipping' : 'Proceed to Shipping'}
              </Button>
            </div>
          </div>
          <div className="d-flex align-items-end" css={{ '.form-group': { width: '100%' } }}>
            <InputComponent
              label="Email address"
              type="text"
              placeholder="Enter email"
              className="form-control w-100"
              value={email}
              handleChange={(e) => setEmail(e.target.value)}
            />
            <Button variant="primary" className="ml-3">
              Send
            </Button>
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col-sm-6 mt-2">
          <Notes
            followUps={followUps}
            orderId={Number(order.id)}
            isAutoSmsAvailable={true}
          />
        </div>
        <div
          className="col-sm-6 d-flex flex-column p-3 mt-2"
          css={{ backgroundColor: '#C2C8CF99' }}>
          <h4>Activity Log</h4>
          {order?.hst?.paperwork?.meta?.sentLinks?.length === 0 ? (
            <span css={{ fontStyle: 'italic' }}>Questionnaire has not yet been sent</span>
          ) : (
            <>
              {order?.hst?.paperwork?.meta?.sentLinks?.map(
                (link: { nonce: string; sentAt: string }) => (
                  <span key={link.nonce}>
                    Questionnaire emailed and sent by text message at{' '}
                    {moment(link.sentAt).format('MM/DD/YYYY HH:mm a')}
                  </span>
                ),
              )}
              {order?.hst?.paperwork?.meta?.finishedAt ? (
                <span>
                  Questionnaire completed at{' '}
                  {moment(order?.hst?.paperwork?.meta?.finishedAt).format('MM/DD/YYYY HH:mm a')}
                </span>
              ) : order?.hst?.paperwork?.meta?.startedAt ? (
                <span css={{}}>
                  Questionnaire is partially completed to:{' '}
                  {
                    completedStepDescription[
                      order?.hst?.paperwork?.meta
                        ?.completedStep as keyof typeof completedStepDescription
                    ]
                  }
                </span>
              ) : (
                <span css={{ fontStyle: 'italic' }}>Questionnaire has not yet been started</span>
              )}
            </>
          )}
          {order?.hst?.paymentSession?.mode === 'payment' &&
          order?.hst?.paymentSession?.status === 'complete' &&
          !isBypassPayment ? (
            <span>Patient Responsibility paid and CC data on file.</span>
          ) : isBypassPayment ? (
            <span css={{ fontStyle: 'italic' }}>
              Stripe payment bypassed (no payment collected).
            </span>
          ) : isPaymentInvalidated ? (
            <span css={{ color: '#ff6b35', fontWeight: 'bold' }}>
              Payment was previously completed but invalidated due to device removal.{' '}
              {order?.hst?.paymentInvalidated
                ? 'Move the order to Device Shipping'
                : 'Use Bypass Stripe'}{' '}
              after re-contacting patient.
            </span>
          ) : order?.hst?.paymentSession?.payment_status === 'no_payment_required' &&
            order?.hst?.paymentSession?.status === 'complete' ? (
            <span css={{ fontStyle: 'italic' }}>Card information saved.</span>
          ) : (
            <span css={{ fontStyle: 'italic' }}>Awaiting Payment ...</span>
          )}
        </div>
      </div>
      {receivePrepaymentOpen && order.patientId && (
        <ReceivePrepaymentModal
          open={receivePrepaymentOpen}
          onClose={() => setReceivePrepaymentOpen(false)}
          patientId={Number(order.patientId)}
          orderId={Number(order.id)}
          stripeInNewTab
        />
      )}
    </div>
  )
}
